import { Zip, ZipDeflate, ZipPassThrough } from 'fflate';

/**
 * A ZIP-unique entry name. `taken` holds LOWER-CASED keys: macOS and Windows
 * extract case-insensitively, so `Skull.png` and `skull.png` would collide (one
 * silently overwriting the other) even though the archive holds both.
 *
 * It lives beside the writer rather than in `batch.ts` because the mockup file
 * names need it too, and `batch.ts` imports those.
 */
export function uniqueName(name: string, taken: Set<string>): string {
  if (!taken.has(name.toLowerCase())) { taken.add(name.toLowerCase()); return name; }
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name, ext = dot > 0 ? name.slice(dot) : '';
  for (let i = 2; ; i++) { const candidate = `${base}-${i}${ext}`; if (!taken.has(candidate.toLowerCase())) { taken.add(candidate.toLowerCase()); return candidate; } }
}

/**
 * Streaming ZIP writer. PNGs are already deflated, so they are STORED (no
 * recompression, no second copy of ~20 MB per file); text entries are deflated.
 * Chunks are collected as fflate emits them and joined once in `finish`.
 */
export function createZip() {
  const chunks: Uint8Array[] = [];
  let failed: Error | null = null;
  const zip = new Zip((err, chunk) => { if (err) failed = err; else chunks.push(chunk); });
  return {
    add(name: string, bytes: Uint8Array, opts: { compress?: boolean; mtime?: Date } = {}): void {
      const entry = opts.compress ? new ZipDeflate(name, { level: 6 }) : new ZipPassThrough(name);
      entry.mtime = opts.mtime ?? new Date();
      zip.add(entry);
      entry.push(bytes, true);
    },
    finish(): Promise<Blob> {
      return new Promise((resolve, reject) => {
        zip.ondata = (err, chunk, final) => {
          if (err) { reject(err); return; }
          chunks.push(chunk);
          if (final) resolve(new Blob(chunks as BlobPart[], { type: 'application/zip' }));
        };
        if (failed) reject(failed); else zip.end();
      });
    },
  };
}
